import React, { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const cleanBaseUrl = BACKEND_URL.replace(/\/api\/?$/, '').replace(/\/$/, '');
const API = `${cleanBaseUrl}/api`;

// ─── Typewriter Effect Hook ──────────────────────────────────────────
function useTypewriter(text, speed = 18, startTyping = false) {
  const [displayed, setDisplayed] = useState('');
  const indexRef = useRef(0);

  useEffect(() => {
    if (!startTyping || !text) { setDisplayed(''); indexRef.current = 0; return; }
    setDisplayed('');
    indexRef.current = 0;
    const interval = setInterval(() => {
      indexRef.current++;
      setDisplayed(text.slice(0, indexRef.current));
      if (indexRef.current >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, startTyping]);

  return displayed;
}

// ─── Animated Score Ring ─────────────────────────────────────────────
function ScoreRing({ score, verdict }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color =
    score >= 80 ? '#22c55e' :
    score >= 60 ? '#eab308' :
    score >= 40 ? '#f97316' : '#ef4444';

  return (
    <div className="flex flex-col items-center gap-3">
      <svg width="132" height="132" className="drop-shadow-sm">
        <circle cx="66" cy="66" r={radius} fill="none" stroke="rgba(6,32,58,0.06)" strokeWidth="10" />
        <circle
          cx="66" cy="66" r={radius} fill="none"
          stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1.5s ease-out', transform: 'rotate(-90deg)', transformOrigin: '66px 66px' }}
        />
        <text x="66" y="62" textAnchor="middle" fill="#06203A" fontSize="28" fontWeight="800" style={{ fontFamily: "'Fraunces', serif" }}>{score}</text>
        <text x="66" y="80" textAnchor="middle" fill="#4C545B" fontSize="11" style={{ fontFamily: "'DM Sans', sans-serif" }}>/ 100</text>
      </svg>
      <span
        className="text-sm font-bold px-3 py-1 rounded-full"
        style={{ background: `${color}15`, color, fontFamily: "'DM Sans', sans-serif" }}
      >
        {verdict}
      </span>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────
export default function AIPeekSimulation() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('chatgpt');
  const [progress, setProgress] = useState(0);

  const chatgptText = useTypewriter(result?.chatgpt || '', 14, !!result && activeTab === 'chatgpt');
  const perplexityText = useTypewriter(result?.perplexity || '', 14, !!result && activeTab === 'perplexity');

  // Simulate progress bar while loading
  useEffect(() => {
    if (!loading) return;
    setProgress(0);
    const steps = [
      { target: 25, delay: 400 },
      { target: 50, delay: 1200 },
      { target: 72, delay: 2500 },
      { target: 88, delay: 4000 },
    ];
    const timers = steps.map(s =>
      setTimeout(() => setProgress(s.target), s.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    setActiveTab('chatgpt');

    try {
      const res = await fetch(`${API}/public/simulate-ai-peek`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Simulation failed');
      }
      const data = await res.json();
      setProgress(100);
      setTimeout(() => setResult(data), 300);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const score = result?.score_analysis;

  return (
    <section className="py-20 bg-[#FAF7F2] relative overflow-hidden" id="live-peek" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Background grid dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.1]"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(6,32,58,0.2) 1px, transparent 0)', backgroundSize: '30px 30px' }}
      />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#06203A]/5 text-[#0C5B94] px-4 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase mb-5">
            <span className="material-symbols-outlined text-base">auto_awesome</span>
            Live Preview
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#06203A] mb-4" style={{ fontFamily: "'Fraunces', serif" }}>
            See How AI Describes Your Business
          </h2>
          <p className="text-lg text-[#4C545B] max-w-2xl mx-auto text-center" style={{ margin: '0 auto' }}>
            Enter any website to instantly preview how ChatGPT and Perplexity would describe it to potential customers.
          </p>
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto mb-12">
          <div className="flex gap-3 p-2 bg-white rounded-2xl border border-[#06203A]/10 shadow-lg shadow-[#06203A]/5">
            <div className="flex-1 flex items-center gap-2 px-4">
              <span className="material-symbols-outlined text-slate-500 text-xl">language</span>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter a website URL (e.g. tesla.com)"
                className="flex-1 bg-transparent text-[#06203A] placeholder-[#4C545B]/50 outline-none focus:outline-none focus:ring-0 border-none text-sm font-medium py-3"
                style={{ outline: 'none', boxShadow: 'none' }}
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !url.trim()}
              className="px-7 py-3 font-semibold text-[0.95rem] transition-all disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              style={{ 
                background: '#FEAB3D', 
                color: '#021426', 
                borderRadius: '0',
                boxShadow: '0 4px 24px rgba(254,171,61,0.35)',
                fontFamily: "'DM Sans', sans-serif"
              }}
            >
              {loading ? 'Simulating...' : 'Simulate'}
            </button>
          </div>
        </form>

        {/* Progress Bar */}
        {loading && (
          <div className="max-w-2xl mx-auto mb-10">
            <div className="h-1.5 bg-[#06203A]/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#0C5B94] to-[#FEAB3D] rounded-full transition-all duration-700 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-[#4C545B] font-medium">
              <span>{progress < 30 ? 'Scraping website...' : progress < 60 ? 'Analyzing content...' : progress < 90 ? 'Running AI simulations...' : 'Finalizing...'}</span>
              <span>{progress}%</span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="max-w-2xl mx-auto mb-8 bg-red-500/10 border border-red-500/30 text-red-300 px-5 py-3 rounded-xl text-sm text-center font-medium">
            {error}
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-[fadeIn_0.5s_ease-out]">
            {/* Left: AI Chat Responses */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-[#06203A]/10 shadow-sm overflow-hidden">
              {/* Tabs */}
              <div className="flex border-b border-[#06203A]/10">
                <button
                  onClick={() => setActiveTab('chatgpt')}
                  className={`flex-1 py-3.5 text-sm font-bold transition-colors ${activeTab === 'chatgpt' ? 'text-[#06203A] bg-[#06203A]/5 border-b-2 border-[#0C5B94]' : 'text-[#4C545B] hover:text-[#06203A]'}`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-base">smart_toy</span>
                    ChatGPT Style
                  </span>
                </button>
                <button
                  onClick={() => setActiveTab('perplexity')}
                  className={`flex-1 py-3.5 text-sm font-bold transition-colors ${activeTab === 'perplexity' ? 'text-[#06203A] bg-[#06203A]/5 border-b-2 border-[#0C5B94]' : 'text-[#4C545B] hover:text-[#06203A]'}`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-base">search</span>
                    Perplexity Style
                  </span>
                </button>
              </div>

              {/* Chat Content */}
              <div className="p-6 min-h-[220px]">
                <div className="flex gap-3 items-start">
                  <div className="w-8 h-8 rounded-lg bg-[#0C5B94]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="material-symbols-outlined text-[#0C5B94] text-base">
                      {activeTab === 'chatgpt' ? 'smart_toy' : 'search'}
                    </span>
                  </div>
                  <div className="text-[#06203A] text-sm leading-relaxed whitespace-pre-wrap">
                    {activeTab === 'chatgpt' ? chatgptText : perplexityText}
                    <span className="inline-block w-0.5 h-4 bg-[#0C5B94] ml-0.5 animate-pulse" />
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="px-6 pb-4">
                <p className="text-xs text-slate-600 italic">
                  ⚠ This is a simulated preview based on your current web presence — not a direct API response from these services.
                </p>
              </div>
            </div>

            {/* Right: Score + Gaps */}
            <div className="flex flex-col gap-6">
              {/* Score Card */}
              <div className="bg-white rounded-2xl border border-[#06203A]/10 p-6 flex flex-col items-center shadow-sm">
                <h4 className="text-xs font-bold text-[#4C545B] uppercase tracking-wider mb-4">AI Visibility Score</h4>
                {score && <ScoreRing score={score.score} verdict={score.verdict} />}
              </div>

              {/* Gaps */}
              {score?.gaps?.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#06203A]/10 p-5 shadow-sm">
                  <h4 className="text-xs font-bold text-[#FF8071] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-sm">warning</span>
                    Missing Data
                  </h4>
                  <ul className="space-y-2">
                    {score.gaps.map((gap, i) => (
                      <li key={i} className="text-sm text-[#4C545B] flex items-start gap-2">
                        <span className="text-[#FF8071] mt-0.5">•</span>
                        {gap}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Strengths */}
              {score?.strengths?.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#06203A]/10 p-5 shadow-sm">
                  <h2 className="text-xs font-bold text-[#11554D] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-sm">check_circle</span>
                    What's Working
                  </h2>
                  <ul className="space-y-2">
                    {score.strengths.map((str, i) => (
                      <li key={i} className="text-sm text-[#4C545B] flex items-start gap-2">
                        <span className="text-[#11554D] mt-0.5">•</span>
                        {str}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
